/**
 * Çoklu döviz gösterimi için ortak yardımcılar.
 *
 * Kur kaynağı: `GET /api/companies/final-rates` → { usdFinalRate, eurFinalRate, gbpFinalRate }
 * Kurlar firma bazlıdır (TCMB + firma markup'ı) ve backend tarafında 3 saatte bir güncellenir.
 * Buradaki TL karşılığı hesaplamaları yalnızca **bilgilendirme** amaçlıdır; kaydedilen
 * hareketlerin TRY karşılığı backend'de (amountTry/exchangeRate) ayrıca snapshot'lanır.
 */

/** final-rates response'undaki alan adları. */
export const FX_RATE_KEYS = { USD: "usdFinalRate", EUR: "eurFinalRate", GBP: "gbpFinalRate" };

export const CURRENCY_SYMBOL = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

/** Tutarı ilgili para biriminin yerel formatında yazar. Örn: ₺89.533,55 / $-7.000,00 */
export const formatCurrencyCode = (amount, code = "TRY") => {
    if (amount == null) return "-";
    return Number(amount).toLocaleString("tr-TR", { style: "currency", currency: code });
};

/**
 * Para biriminin geçerli kurunu döner. TRY için 1, kur tanımsız/geçersizse null.
 * null dönmesi "TL karşılığı gösterilemez" demektir — asla varsayılan kur uydurulmaz.
 */
export const getFxRate = (currency, finalRates) => {
    const code = (currency || "TRY").toUpperCase();
    if (code === "TRY") return 1;
    const rate = Number(finalRates?.[FX_RATE_KEYS[code]]);
    return Number.isFinite(rate) && rate > 0 ? rate : null;
};

/** Tutarın TL karşılığı. Kur yoksa null. */
export const toTry = (amount, currency, finalRates) => {
    const value = Number(amount);
    if (!Number.isFinite(value)) return null;
    const rate = getFxRate(currency, finalRates);
    return rate == null ? null : value * rate;
};

/** Kur rozeti metni. Örn: "1$ = ₺38,50" */
export const formatFxRate = (currency, rate) => {
    if (rate == null) return null;
    const symbol = CURRENCY_SYMBOL[(currency || "").toUpperCase()] || currency;
    const value = Number(rate).toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    });
    return `1${symbol} = ₺${value}`;
};

/**
 * Döviz bazlı bakiye listesinin TL karşılığı toplamlarını hesaplar.
 * Kuru tanımlı olmayan para birimleri toplama dahil edilmez, `unconvertedCurrencies` ile bildirilir.
 */
export const summarizeBalancesInTry = (balances, finalRates) => {
    const list = Array.isArray(balances) ? balances : [];
    const unconvertedCurrencies = [];
    let netBalance = 0;
    let totalDebt = 0;
    let totalReceipt = 0;
    let convertedCount = 0;

    list.forEach((b) => {
        const rate = getFxRate(b.currency, finalRates);
        if (rate == null) {
            unconvertedCurrencies.push(b.currency);
            return;
        }
        convertedCount += 1;
        netBalance += Number(b.currentBalance || 0) * rate;
        totalDebt += Number(b.totalDebtAmount || 0) * rate;
        totalReceipt += Number(b.totalReceiptAmount || 0) * rate;
    });

    const hasConverted = convertedCount > 0;
    return {
        hasConverted,
        netBalance: hasConverted ? netBalance : null,
        totalDebt: hasConverted ? totalDebt : null,
        totalReceipt: hasConverted ? totalReceipt : null,
        unconvertedCurrencies,
    };
};
